import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCounsellorList } from "../../api/Data/Counsellor/index";
import { ChevronRight, UserCheck } from "lucide-react";

const PractitionerRow = ({ practitioner, isLast }) => {
  const fullName = `${practitioner.title || 'Dr'}. ${practitioner.firstName || ''} ${practitioner.lastName || ''}`.trim();

  const handleNavigateToDetail = () => {
    // Use the appropriate ID field for navigation - prioritize pcUserId, then id, then practitionerId
    const practitionerId = practitioner.pcUserId || practitioner.id || practitioner.practitionerId;
    if (practitionerId) {
      window.location.href = `/practitioner-detail/${practitionerId}`;
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-poppins font-medium text-[14px] text-[#171717] truncate">
            {fullName}
          </span>
          <span className="font-poppins text-[11px] font-semibold text-[#059669] bg-[#ECFDF5] rounded-full px-2.5 py-0.5 flex-shrink-0">
            Available
          </span>
        </div>
        <div
          className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] transition-colors cursor-pointer flex-shrink-0"
          onClick={handleNavigateToDetail}
        >
          <ChevronRight size={14} className="text-[#64748B]" />
        </div>
      </div>
      {!isLast && <div className="h-px w-full bg-[#F1F5F9]"></div>}
    </>
  );
};

export default function AvailablePractitionersSection() {
  const dispatch = useDispatch();
  const { data: counsellorData, loading } = useSelector((state) => state.GET_COUNSELLOR_LIST);

  const handleViewAll = () => {
    window.location.href = "/practitioner";
  };

  // Data is fetched once by the Dashboard (useDashboardData); dispatch is
  // only used by the Retry button below

  // Filter practitioners available today with improved error handling
  const getAvailablePractitioners = () => {
    try {
      if (!counsellorData?.data?.payload?.data || !Array.isArray(counsellorData.data.payload.data)) {
        return [];
      }

      const today = new Date()
        .toLocaleString("en-US", { weekday: "short" })
        .toUpperCase();

      const availableToday = counsellorData.data.payload.data.filter(
        (counsellor) => {
          // More robust availability check
          return counsellor.availableDay &&
                 (typeof counsellor.availableDay === 'string' ?
                  counsellor.availableDay.includes(today) :
                  Array.isArray(counsellor.availableDay) && counsellor.availableDay.includes(today));
        }
      );

      return availableToday;
    } catch (error) {
      // Fallback: show all practitioners if filtering fails
      return counsellorData?.data?.payload?.data || [];
    }
  };

  const availablePractitioners = getAvailablePractitioners();
  const displayPractitioners = availablePractitioners.slice(0, 2);

  return (
    <div className="bg-white rounded-2xl flex-1 p-[24px] border-[1px] border-solid border-[#E2E8F0] shadow-sm">
      <div className="flex items-center justify-between gap-2 -mx-[24px] -mt-[24px] px-[24px] py-[14px] bg-[#E7EDF5] rounded-t-[15px] mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-white text-[#0D9488] flex-shrink-0">
            <UserCheck size={18} />
          </div>
          <h3 className="font-poppins font-semibold text-base text-[#171717] mb-0">
            Available Practitioners
          </h3>
        </div>
        <button
          type="button"
          className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap text-[#2563EB] font-poppins text-xs font-semibold bg-white hover:bg-[#F8FAFC] rounded-full px-3 py-1.5 border-0 transition-colors"
          onClick={handleViewAll}
        >
          View all ({availablePractitioners.length})
          <ChevronRight size={14} />
        </button>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-center text-[#64748B] text-sm font-poppins">Loading...</div>
        ) : counsellorData?.status === "error" ? (
          <div className="text-center text-[#64748B] text-sm font-poppins">
            <div>Unable to load practitioners</div>
            <button
              onClick={() => {
                const token = localStorage.getItem("token");
                if (token) dispatch(getCounsellorList("Bearer " + token));
              }}
              className="text-[#2563EB] font-semibold bg-transparent border-0 hover:underline text-sm mt-1"
            >
              Retry
            </button>
          </div>
        ) : displayPractitioners.length > 0 ? (
          displayPractitioners.map((practitioner, index) => (
            <PractitionerRow
              key={practitioner.id || practitioner.practitionerId || index}
              practitioner={practitioner}
              isLast={index === displayPractitioners.length - 1}
            />
          ))
        ) : counsellorData?.data?.payload?.data?.length > 0 ? (
          <div className="text-center text-[#64748B] text-sm font-poppins">No practitioners available today</div>
        ) : (
          <div className="text-center text-[#64748B] text-sm font-poppins">No practitioners found</div>
        )}
      </div>
    </div>
  );
}